import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DEFAULT_LANGUAGE, EMPTY_ARRAY_LENGTH, IFormDelegate, IFormOptions } from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { ILegalFormDTO, ILegalFormExtendedDTO } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { Subject, filter } from 'rxjs';
import { ILegalformFormData } from '../../interfaces/legal-form-form-data.interface';
import { getDebug } from '../../utils/debugUtil';
import { getLocalizationFromAsyncPipe } from '../../utils/localizationUtils';

const filename = 'projects/aumico-lib-settings-fe/src/lib/components/legal-form-form/legal-form-form.component.ts';
const debug = getDebug(filename);

@UntilDestroy()
@Component({
    // tslint:disable-next-line: component-selector
    selector: 'aumico-settings-legal-form-form',
    templateUrl: './legal-form-form.component.html',
    styleUrls: ['./legal-form-form.component.scss'],
})
export class LegalFormFormComponent implements OnInit, IFormDelegate {
    countries: INameId[] = [];
    countryISO31661Alpha2CodesFormControl = new FormControl(null);
    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: ILegalformFormData,
        private i18nextService: I18NextService,
    ) {
        this.countries = data.countries;
        const legalForm = this.data.legalForm;

        if (legalForm != null) {
            this.setCountryISO31661Alpha2CodesFormControlValue(data.legalForm);
        }

        this.handleItemUpdated();
    }

    @Input() languages: string[] = ['de-ch', 'en-us'];

    // @Output() public event = new EventEmitter<{success}>();

    @ViewChild('createUpdateForm', { static: false })
    public createUpdateForm: ElementRef;

    protected subscriptions = [];

    options: IFormOptions;

    public savePressed = new Subject<ILegalFormDTO>();
    public nameAlreadyExistsError = false;

    public ngOnInit(): void {
        this.data.errorMessage
            .pipe(
                filter((legalFormResponse) => legalFormResponse != null && legalFormResponse.status === 409),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.nameAlreadyExistsError = true;
            });
        this.prepareFields();
    }

    private setCountryISO31661Alpha2CodesFormControlValue(legalForm: ILegalFormExtendedDTO): void {
        const countryISO31661Alpha2Codes = legalForm.countryISO31661Alpha2Codes;
        if (countryISO31661Alpha2Codes == null && countryISO31661Alpha2Codes.length === EMPTY_ARRAY_LENGTH) {
            return;
        }

        const selectedCountries = this.countries.filter((country) => countryISO31661Alpha2Codes.includes(country.id));
        this.countryISO31661Alpha2CodesFormControl.setValue(selectedCountries);
    }

    handleItemUpdated() {
        if (this.data.legalForm != null) {
            this.data.legalForm.text = this.data.legalForm.text;
            for (const lang of this.languages) {
                this.data.legalForm.text[lang] = this.data.legalForm.text[lang] || '';
            }
            debug('ngOnInit getAll result');
            debug(this.data);
            this.prepareFields(this.data.legalForm._id == null ? 'create' : 'edit');
        }
    }

    public onSubmit(result: ILegalFormExtendedDTO): void {
        debug('result of the form');
        debug(result);
        const selectedCountries: INameId[] = [];

        const countryISO31661Alpha2CodesFormControlValue = this.countryISO31661Alpha2CodesFormControl.value;
        if (countryISO31661Alpha2CodesFormControlValue != null) {
            selectedCountries.push(...(countryISO31661Alpha2CodesFormControlValue as INameId[]));
        }
        // map ILegalFormExtendedDTO to ILegalFormDTO
        const legalFormToSave: ILegalFormDTO = {
            _id: result._id ? result._id : undefined,
            name: result.name,
            text: result.text,
            countryISO31661Alpha2Codes: selectedCountries.map((country) => country.id),
        };
        this.savePressed.next(legalFormToSave);
    }

    async prepareFields(action: 'create' | 'edit' = 'create') {
        this.options = {
            properties: [
                {
                    name: await getLocalizationFromAsyncPipe(this.i18nextService, 'aumico_settings_legalForm_id'),
                    type: 'label',
                    property: '_id',
                    required: false,
                },
                {
                    name: await getLocalizationFromAsyncPipe(this.i18nextService, 'aumico_settings_legalForm_version'),
                    type: 'label',
                    property: 'version',
                    required: false,
                },
                {
                    name: await getLocalizationFromAsyncPipe(this.i18nextService, 'aumico_settings_legalForm_owner'),
                    type: 'label',
                    property: 'owner',
                    required: false,
                },
                {
                    name: await getLocalizationFromAsyncPipe(this.i18nextService, 'aumico_settings_legalForm_name'),
                    type: action === 'create' ? 'text' : 'label',
                    property: 'name',
                    required: true,
                },
                {
                    name: await getLocalizationFromAsyncPipe(this.i18nextService, 'aumico_settings_legalForm_text'),
                    type: 'translatable-text',
                    property: 'text',
                    required: true,
                    languages: [DEFAULT_LANGUAGE],
                },
            ],
            submitText: await getLocalizationFromAsyncPipe(this.i18nextService, 'aumico_button_save'),
        };
    }
}
